/**
 * Calendário (17 §3.3, D108): sessões, prazos de impugnação e esclarecimento, vencimentos de certidões, assinaturas
 * de portal e cotações em uso. Visões mês, semana e lista; cada evento leva à ficha correspondente.
 */
import { useMemo, useState } from "react";
import { Title } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import AnteriorIcon from "@mui/icons-material/ChevronLeft";
import ProximoIcon from "@mui/icons-material/ChevronRight";
import { supabase } from "../lib/supabase";
import { tokens } from "../tema";

export interface EventoCalendario {
  tipo: "sessao" | "impugnacao" | "certidao" | "assinatura_portal" | "cotacao_vencida";
  entidade_id: string;
  data: string;
  hora: string | null;
  titulo: string;
  situacao: string | null;
  link: string | null;
}

export const TIPOS_EVENTO: Record<EventoCalendario["tipo"], { nome: string; cor: string }> = {
  sessao: { nome: "Sessão", cor: tokens.ciano },
  impugnacao: { nome: "Impugnação / esclarecimento", cor: "#b26a00" },
  certidao: { nome: "Certidão", cor: "#7b1fa2" },
  assinatura_portal: { nome: "Assinatura de portal", cor: tokens.navy },
  cotacao_vencida: { nome: "Cotação", cor: "#c62828" },
};

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

/** Data local (sem fuso) em AAAA-MM-DD. */
export const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const somarDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const dataBr = (s: string) => s.split("-").reverse().join("/");

export function useEventos(inicio: string, fim: string) {
  return useQuery({
    queryKey: ["v_calendario", inicio, fim],
    queryFn: async () => {
      const { data, error } = await supabase.from("v_calendario").select("*").gte("data", inicio).lte("data", fim).order("data").order("hora", { nullsFirst: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as EventoCalendario[];
    },
  });
}

/** Licitação descartada, suspensa ou já encerrada aparece apagada. */
const encerrado = (e: EventoCalendario) =>
  (e.tipo === "sessao" || e.tipo === "impugnacao") && ["descartada", "suspensa", "resultado", "recurso", "homologada"].includes(e.situacao ?? "");

function ChipEvento({ e }: { e: EventoCalendario }) {
  const navigate = useNavigate();
  const t = TIPOS_EVENTO[e.tipo];
  return (
    <Box
      role="button"
      tabIndex={0}
      title={`${t.nome}: ${e.titulo}`}
      onClick={() => e.link && navigate(e.link)}
      onKeyDown={(k) => k.key === "Enter" && e.link && navigate(e.link)}
      sx={{
        fontSize: 11,
        lineHeight: 1.3,
        px: 0.5,
        py: 0.25,
        mb: 0.25,
        borderLeft: `3px solid ${t.cor}`,
        bgcolor: "background.default",
        borderRadius: 0.5,
        cursor: e.link ? "pointer" : "default",
        opacity: encerrado(e) ? 0.45 : 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {e.hora ? `${e.hora} ` : ""}
      {e.titulo}
    </Box>
  );
}

function Grade({ dias, eventos, mesAtual }: { dias: Date[]; eventos: EventoCalendario[]; mesAtual: number | null }) {
  const hoje = iso(new Date());
  const porDia = useMemo(() => {
    const m = new Map<string, EventoCalendario[]>();
    for (const e of eventos) m.set(e.data, [...(m.get(e.data) ?? []), e]);
    return m;
  }, [eventos]);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      {DIAS_SEMANA.map((d) => (
        <Box key={d} sx={{ p: 0.5, textAlign: "center", fontSize: 12, fontWeight: 700, bgcolor: "background.default", borderBottom: 1, borderColor: "divider" }}>
          {d}
        </Box>
      ))}
      {dias.map((d) => {
        const chave = iso(d);
        const lista = porDia.get(chave) ?? [];
        const fora = mesAtual !== null && d.getMonth() !== mesAtual;
        return (
          <Box
            key={chave}
            sx={{
              minHeight: mesAtual === null ? 160 : 96,
              p: 0.5,
              borderRight: 1,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: chave === hoje ? "rgba(0,176,179,.06)" : "background.paper",
              opacity: fora ? 0.5 : 1,
              minWidth: 0,
            }}
          >
            <Typography variant="caption" fontWeight={chave === hoje ? 700 : 400} color={chave === hoje ? "secondary" : "text.secondary"}>
              {d.getDate()}
            </Typography>
            {lista.slice(0, mesAtual === null ? 12 : 4).map((e) => (
              <ChipEvento key={`${e.tipo}-${e.entidade_id}-${e.titulo}`} e={e} />
            ))}
            {mesAtual !== null && lista.length > 4 && (
              <Typography variant="caption" color="text.secondary">
                + {lista.length - 4}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export function ListaEventos({ eventos, vazio }: { eventos: EventoCalendario[]; vazio: string }) {
  const navigate = useNavigate();
  if (eventos.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        {vazio}
      </Typography>
    );
  let dataAnterior = "";
  return (
    <Stack spacing={0.5}>
      {eventos.map((e) => {
        const cabecalho = e.data !== dataAnterior;
        dataAnterior = e.data;
        const t = TIPOS_EVENTO[e.tipo];
        return (
          <Box key={`${e.tipo}-${e.entidade_id}-${e.data}-${e.titulo}`}>
            {cabecalho && (
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {dataBr(e.data)} · {DIAS_SEMANA[new Date(`${e.data}T12:00:00`).getDay()]}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => e.link && navigate(e.link)}
              sx={{ cursor: e.link ? "pointer" : "default", opacity: encerrado(e) ? 0.5 : 1, py: 0.25 }}
            >
              <Chip size="small" label={t.nome} sx={{ bgcolor: t.cor, color: "#fff", minWidth: 0 }} />
              <Typography variant="body2" sx={{ minWidth: 0 }}>
                {e.hora ? `${e.hora} · ` : ""}
                {e.titulo}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

type Visao = "mes" | "semana" | "lista";

export function Calendario() {
  const celular = useMediaQuery((t: Theme) => t.breakpoints.down("sm"));
  const [visao, setVisao] = useState<Visao>(celular ? "lista" : "mes");
  const [ref, setRef] = useState(() => new Date());
  const [filtro, setFiltro] = useState<Set<EventoCalendario["tipo"]>>(new Set(Object.keys(TIPOS_EVENTO) as EventoCalendario["tipo"][]));

  // Período visível.
  const { dias, inicio, fim, titulo } = useMemo(() => {
    if (visao === "mes") {
      const primeiro = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const ini = somarDias(primeiro, -primeiro.getDay());
      const lista = Array.from({ length: 42 }, (_, i) => somarDias(ini, i));
      return { dias: lista, inicio: iso(lista[0] ?? ini), fim: iso(lista[41] ?? ini), titulo: `${MESES[ref.getMonth()]} de ${ref.getFullYear()}` };
    }
    if (visao === "semana") {
      const ini = somarDias(ref, -ref.getDay());
      const lista = Array.from({ length: 7 }, (_, i) => somarDias(ini, i));
      const ult = lista[6] ?? ini;
      return { dias: lista, inicio: iso(ini), fim: iso(ult), titulo: `${dataBr(iso(ini))} a ${dataBr(iso(ult))}` };
    }
    const ini = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
    const ult = somarDias(ini, 59);
    return { dias: [], inicio: iso(ini), fim: iso(ult), titulo: `${dataBr(iso(ini))} a ${dataBr(iso(ult))}` };
  }, [visao, ref]);

  const { data: eventos = [], error, isPending } = useEventos(inicio, fim);
  const visiveis = eventos.filter((e) => filtro.has(e.tipo));

  function mover(n: number) {
    if (visao === "mes") setRef(new Date(ref.getFullYear(), ref.getMonth() + n, 1));
    else setRef(somarDias(ref, n * (visao === "semana" ? 7 : 60)));
  }

  return (
    <Card sx={{ mt: 2 }}>
      <Title title="Calendário" />
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton aria-label="Anterior" onClick={() => mover(-1)}>
              <AnteriorIcon />
            </IconButton>
            <Button size="small" onClick={() => setRef(new Date())}>
              Hoje
            </Button>
            <IconButton aria-label="Próximo" onClick={() => mover(1)}>
              <ProximoIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontSize: "1rem", textTransform: "capitalize", ml: 1 }}>
              {titulo}
            </Typography>
          </Stack>
          <ToggleButtonGroup size="small" exclusive value={visao} onChange={(_, v: Visao | null) => v && setVisao(v)}>
            <ToggleButton value="mes">Mês</ToggleButton>
            <ToggleButton value="semana">Semana</ToggleButton>
            <ToggleButton value="lista">Lista</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {(Object.entries(TIPOS_EVENTO) as [EventoCalendario["tipo"], { nome: string; cor: string }][]).map(([tipo, t]) => {
            const ligado = filtro.has(tipo);
            return (
              <Chip
                key={tipo}
                size="small"
                label={t.nome}
                onClick={() => {
                  const novo = new Set(filtro);
                  if (ligado) novo.delete(tipo);
                  else novo.add(tipo);
                  setFiltro(novo);
                }}
                variant={ligado ? "filled" : "outlined"}
                sx={ligado ? { bgcolor: t.cor, color: "#fff" } : { borderColor: t.cor, color: t.cor }}
              />
            );
          })}
        </Stack>
        {error && <Alert severity="error">{error.message}</Alert>}
        {visao === "lista" ? (
          <ListaEventos eventos={visiveis} vazio={isPending ? "Carregando…" : "Nada no período."} />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 560 }}>
              <Grade dias={dias} eventos={visiveis} mesAtual={visao === "mes" ? ref.getMonth() : null} />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
